import React, { useEffect, useMemo, useState } from "react";
import { FlatList, Platform, View } from "react-native";
import { useSelector } from "react-redux";
import styled from "styled-components/native";
import ProductDetailItem from "../ProductDetailItem/ProductDetailItem";
import MatchExpandDetailView from "../MatchExpandDetailView/MatchExpandDetailView";
import MatchPriceDescriptionView from "../MatchPriceDescriptionView/MatchPriceDescriptionView";
import MatchProductDetailView from "../MatchProductDetailView/MatchProductDetailView";
import MatchSupplierCommentView from "../MatchSupplierCommentView/MatchSupplierCommentView";
import MatchSupplierDetailView from "../MatchSupplierDetailView/MatchSupplierDetailView";
import MatchSupplierLocationView from "../MatchSupplierLocationView/MatchSupplierLocationView";
import ProductHeaderView from "../ProductHeaderView/ProductHeaderView";
import { useUserCurrency } from "../../../../hooks/getCurrency";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import {
  CustomtOption,
  PdpSections,
  PdpComponentNames,
} from "@utils/customization";
import {
  useGetPdpSectionLayout,
  useGetFindMatchSectionsOrder,
} from "../../../../hooks/useGetSectionLayout";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CustomisationKey } from "@scenes/pdp/types";
import { useFindMatch } from "@scenes/findMatch/FindMatchContext";
import {
  getTotalOtherDetailsArray,
  getTotalProductDetailsArray,
} from "./defaultList";
import {
  checkValidationValue,
  dataMapValidateValue,
  filterDetailsArray,
  getAvailabilityStatus,
} from "./utils";
import { ProductCategory } from "@utils/constants";
import { useFilteredOptions } from "../../../../hooks/useFilteredOptions";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getVisibleMatchPairSections } from "@scenes/pdp/utils/pdpConfigurableComponent";
import {
  useAppSettings,
  useSupplierSpecificCommentInOrangeMode,
} from "@redux/slices/globalSlice";

const ProductDetailList = () => {
  const {
    theme,
    t,
    userInfo,
    currentIndex,
    itemDetail,
    otherDetail,
    productType,
    productColor,
  } = useFindMatch();
  const { isVDBOrg } = useOrgSettingsSlice();
  const embedDetails = Platform.OS === "web" && !!useUserInfo().isIFrame();
  const dateFormat = "MMM D, YYYY [at] hh:mm A";
  const userCurrency = useUserCurrency();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const [localSelectedLocation, setLocalSelectedLocation] = useState(null);
  const [otherSelectedLocation, setOtherSelectedLocation] = useState(null);
  const [showNumber, setShowPhoneNumber] = useState(false);
  const [MoreNumber, setMoreNumber] = useState(true);
  const priceMode = userInfo?.user?.price_mode;
  const getAppSettings = useAppSettings();
  const hideCertOnMatchPair = getAppSettings?.hideCertOnMatchPair ?? false;

  const matchDetailPairLayout = useGetPdpSectionLayout(
    PdpSections.MATCH_PAIR_PRODUCT_DETAILS,
    productType
  );
  const supplierSectionLayout = useGetPdpSectionLayout(
    PdpSections.SUPPLIER_DETAILS,
    productType
  );

  const commentsLayout = useGetPdpSectionLayout(
    PdpSections.COMMENTS,
    productType
  );
  const showCommentSection = commentsLayout?.price_modes?.includes(priceMode);

  const showSupplierDetails =
    supplierSectionLayout?.price_modes?.includes(priceMode);

  const supplierCommentsLayout = useGetPdpSectionLayout(
    PdpSections.SUPPLIER_COMMENT,
    productType
  );
  // Get configurable sections order from Find Match JSON config
  const findMatchSectionsConfig = useGetFindMatchSectionsOrder(productType);
  const visibleSections = useMemo(
    () => getVisibleMatchPairSections(findMatchSectionsConfig, priceMode),
    [findMatchSectionsConfig, priceMode]
  );

  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);
  const supplierSpecificCommentInOrangeMode =
    useSupplierSpecificCommentInOrangeMode();

  const filteredOptions = useFilteredOptions(
    theme,
    priceMode,
    matchDetailPairLayout
  );

  useEffect(() => {
    setCustomOptions(filteredOptions);
  }, [filteredOptions]);

  const totalProductDetailsArray = getTotalProductDetailsArray(
    t,
    itemDetail,
    userInfo,
    priceMode,
    dateFormat,
    userCurrency,
    isCustomApp,
    MoreNumber,
    showNumber,
    localSelectedLocation,
    otherSelectedLocation,
    supplierSpecificCommentInOrangeMode
  );

  const totalOtherDetailsArray = getTotalOtherDetailsArray(
    otherDetail,
    currentIndex,
    userInfo,
    dateFormat,
    userCurrency,
    isCustomApp,
    showNumber,
    localSelectedLocation,
    otherSelectedLocation,
    priceMode,
    supplierSpecificCommentInOrangeMode,
    t
  );
  const productDetailsArray =
    priceMode === 0 || priceMode === 2
      ? filterDetailsArray(totalProductDetailsArray, priceMode, t)
      : totalProductDetailsArray;

  const otherDetailsArray =
    priceMode === 0 || priceMode === 2
      ? filterDetailsArray(totalOtherDetailsArray, priceMode, t)
      : totalOtherDetailsArray;

  const getCustomArray = (isPrimary: boolean) => {
    if (customOptions.length === 0) {
      if (isPrimary) {
        return productDetailsArray;
      } else {
        return otherDetailsArray;
      }
    }

    const shouldShowNumber = (option: any) => {
      const showForTypes = ["clickToShow", "hideview", "socialmedia"];
      const showForNames = [
        "total_sales_price",
        "price_per_carat",
        "discount_percent",
        "available",
        "last_updated_at",
      ];

      if (
        itemDetail?.is_fancy &&
        option?.name === CustomisationKey.discount_percent
      ) {
        return false;
      }

      if (
        showForTypes.includes(option?.type) ||
        showForNames.includes(option?.name)
      ) {
        if (showForNames.includes(option?.name)) {
          return priceMode !== PriceModes.GREEN;
        }
        return option?.type === "clickToShow"
          ? MoreNumber
          : checkValidationValue(
              itemDetail?.locations,
              option?.name,
              showNumber
            );
      }

      return true;
    };

    return customOptions.map((option, index) => ({
      id: index + 1,
      name: t(option?.name),
      title: t(option?.label),
      description:
        option?.type === "space" || option?.type === "title"
          ? ""
          : option?.type === "clickToShow"
          ? t(option.label)
          : dataMapValidateValue(
              option?.name,
              option?.type,
              isPrimary,
              isCustomApp,
              itemDetail,
              otherDetail,
              currentIndex,
              localSelectedLocation,
              otherSelectedLocation,
              productType === ProductCategory.JEWELRY,
              dateFormat,
              userCurrency,
              userInfo,
              hideCertOnMatchPair,
              priceMode,
              supplierSpecificCommentInOrangeMode,
              t
            ),
      icon:
        priceMode !== PriceModes.GREEN && option?.type === "text_with_icon"
          ? getAvailabilityStatus(
              isPrimary ? itemDetail : otherDetail?.[currentIndex]
            )
          : option?.icon?.data,
      showNumber: shouldShowNumber(option),
      clickToShow: option?.type === "clickToShow" ? true : false,
      price_modes: option.price_modes,
      order: option.order || 0,
      isTitle: option?.name === "short_title" ? true : false,
      hideDivider: option?.name === "short_title" ? true : false,
      WhatsApp: checkValidationValue(
        itemDetail?.locations,
        "contact_whatsapp",
        showNumber
      ),
      WhatsAppCountry: checkValidationValue(
        itemDetail?.locations,
        "contact_whatsapp_country_code",
        showNumber
      ),
      FaceTime: checkValidationValue(
        itemDetail?.locations,
        "contact_iphone",
        showNumber
      ),
      FaceTimeCountry: checkValidationValue(
        itemDetail?.locations,
        "contact_iphone_country_code",
        showNumber
      ),
      color:
        option?.name === "office_phone" || option?.name === "mobile_phone"
          ? "link-static"
          : undefined,
      clickable:
        option?.name === "office_phone" || option?.name === "mobile_phone"
          ? true
          : false,
      isLocation:
        option?.type === "locations" && option.name === "vendor_name"
          ? true
          : false,
    }));
  };

  useEffect(() => {
    if (itemDetail && Array.isArray(itemDetail?.locations)) {
      const primaryLocation = itemDetail?.locations.find(
        (item) => item.is_primary
      );

      setLocalSelectedLocation(primaryLocation);
    }

    if (itemDetail && Array.isArray(otherDetail?.[currentIndex]?.locations)) {
      const otherPrimaryLocation = otherDetail?.[currentIndex]?.locations.find(
        (item) => item.is_primary
      );
      setOtherSelectedLocation(otherPrimaryLocation);
    }
  }, [itemDetail, otherDetail]);

  const otherDetails = getCustomArray(false);

  const renderMatchPairComponent = (componentName: string) => {
    switch (componentName) {
      case PdpComponentNames.MATCH_HEADER:
        return (
          <ProductHeaderView
            key={componentName}
            itemDetail={itemDetail}
            priceMode={priceMode}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
            productType={productType}
            productColor={productColor}
          />
        );
      case PdpComponentNames.MATCH_PRICE_DESCRIPTION:
        return priceMode !== 0 ? (
          <MatchPriceDescriptionView
            key={componentName}
            itemDetail={itemDetail}
            priceMode={priceMode}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
            productType={productType}
          />
        ) : null;
      case PdpComponentNames.MATCH_PRODUCT_DETAILS:
        return (
          <MatchProductDetailView
            key={componentName}
            itemDetail={itemDetail}
            priceMode={priceMode}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
          />
        );
      case PdpComponentNames.COMMENTS:
        return (
          Object.keys(commentsLayout)?.length > 0
            ? showCommentSection
            : priceMode === PriceModes.WHITE
        ) ? (
          <MatchExpandDetailView
            key={componentName}
            itemDetail={itemDetail}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
            priceMode={priceMode}
          />
        ) : null;
      case PdpComponentNames.SUPPLIER_CONTAINER:
        return priceMode === PriceModes.WHITE && showSupplierDetails ? (
          <MatchSupplierDetailView
            key={componentName}
            itemDetail={itemDetail}
            priceMode={priceMode}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
          />
        ) : null;
      case PdpComponentNames.SUPPLIER_INFO:
        return priceMode === PriceModes.WHITE && showSupplierDetails ? (
          <MatchSupplierLocationView
            key={componentName}
            itemDetail={itemDetail}
            priceMode={priceMode}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
            productColor={productColor}
          />
        ) : null;
      case PdpComponentNames.SUPPLIER_COMMENT:
        return (supplierCommentsLayout?.price_modes?.includes(priceMode) ||
          isVDBOrg) &&
          itemDetail?.supplier_specific_comment &&
          priceMode === PriceModes.WHITE ? (
          <MatchSupplierCommentView
            key={componentName}
            itemDetail={itemDetail}
            otherDetail={otherDetail}
            currentIndex={currentIndex}
            priceMode={priceMode}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <View>
      {theme.isMobile ? (
        <View>
          {embedDetails ? (
            getCustomArray(true).map((item, index) => (
              <ProductDetailItem
                key={item.id}
                itemDetail={itemDetail}
                localSelectedLocation={localSelectedLocation}
                item={item}
                index={index}
                otherDetailsArray={otherDetails}
                otherDetail={otherDetail}
                setLocalSelectedLocation={setLocalSelectedLocation}
                setOtherSelectedLocation={setOtherSelectedLocation}
                currentIndex={currentIndex}
                otherSelectedLocation={otherSelectedLocation}
                setShowPhoneNumber={setShowPhoneNumber}
                setMoreNumber={setMoreNumber}
                productColor={productColor}
                isFromMatchPair
              />
            ))
          ) : (
            <FlatList
              data={getCustomArray(true)}
              renderItem={({ item, index }) => (
                <ProductDetailItem
                  key={item.id}
                  itemDetail={itemDetail}
                  localSelectedLocation={localSelectedLocation}
                  item={item}
                  index={index}
                  otherDetailsArray={otherDetails}
                  otherDetail={otherDetail}
                  setLocalSelectedLocation={setLocalSelectedLocation}
                  setOtherSelectedLocation={setOtherSelectedLocation}
                  currentIndex={currentIndex}
                  otherSelectedLocation={otherSelectedLocation}
                  setShowPhoneNumber={setShowPhoneNumber}
                  setMoreNumber={setMoreNumber}
                  productColor={productColor}
                  isFromMatchPair
                />
              )}
              keyExtractor={(item) => item.id.toString()}
              scrollEnabled={theme.isLargeScreen ? false : true}
            />
          )}

          {Platform.OS === "ios" && <Spacer size={SpaceType.Size_80} />}
        </View>
      ) : (
        <View>
          {visibleSections.map((componentName) =>
            renderMatchPairComponent(componentName)
          )}
          <FooterSpace />
        </View>
      )}
    </View>
  );
};

const FooterSpace = styled.View`
  padding: 10px;
  height: 100px;
`;

export default ProductDetailList;
